/* ================================================
   EUCALOO THEME — variant-picker.js
   Handles color + size variant selection, price/availability updates
   ================================================ */

(() => {
  'use strict';

  /* ---- Color name → CSS hex map ---- */
  const COLOR_MAP = {
    red: '#E53935', coral: '#F07060', orange: '#F0A830', amber: '#FFCA28',
    yellow: '#FFD600', lime: '#B8D840', green: '#43A047', teal: '#4CBFBF',
    cyan: '#00BCD4', sky: '#29B6F6', blue: '#4A8EC2', navy: '#1A237E',
    indigo: '#5C6BC0', violet: '#7E57C2', purple: '#C088D0', pink: '#F05A96',
    fuchsia: '#E91E8C', rose: '#F48FB1', white: '#FFFFFF', black: '#000000',
    grey: '#9E9E9E', gray: '#9E9E9E', beige: '#F5F0E8', cream: '#FFFDE7',
    brown: '#795548', khaki: '#BDB76B', gold: '#FFD700', silver: '#C0C0C0',
    ivory: '#FFFFF0', lavender: '#E6E0F8', mint: '#B2EBE0',
  };

  function getColorHex(name) {
    if (!name) return '#CCCCCC';
    const lower = name.toLowerCase().trim();
    if (COLOR_MAP[lower]) return COLOR_MAP[lower];
    // Try to find partial match
    for (const [key, val] of Object.entries(COLOR_MAP)) {
      if (lower.includes(key)) return val;
    }
    return '#CCCCCC';
  }

  /* ---- Format Money ---- */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }

  /* ---- Variant Picker Class ---- */
  class VariantPicker {
    constructor(container) {
      this.container   = container;
      this.productHandle = container.dataset.productHandle;
      this.productData   = null;
      this.currentVariant = null;

      this.priceEl     = container.closest('.product-info')?.querySelector('[data-product-price]');
      this.compareEl   = container.closest('.product-info')?.querySelector('[data-compare-price]');
      this.atcBtn      = document.querySelector('[data-atc-btn]');
      this.variantInput = document.querySelector('[name="id"]');
      this.stockMsg    = document.querySelector('[data-stock-msg]');
      this.qtyWrapper  = document.querySelector('[data-qty-wrapper]');
      this.atcSection  = document.querySelector('.product-atc-section');
      this.galleryMain  = document.querySelector('.product-gallery__main img');

      this.selections = {};
      this.inventoryMap = {};

      // Load inventory quantities embedded by Liquid
      const productId = container.dataset.productId;
      const invScript = document.getElementById(`VariantInventory-${productId}`);
      if (invScript) {
        try { this.inventoryMap = JSON.parse(invScript.textContent); } catch (e) {}
      }

      this.loadProductData();
    }

    async loadProductData() {
      try {
        const res = await fetch(`/products/${this.productHandle}.js`);
        this.productData = await res.json();
        // Normalize options: Shopify may return strings or {name, position, values} objects
        this.productData.options = this.productData.options.map(o =>
          (o !== null && typeof o === 'object') ? o.name : o
        );
        this.currentVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
        this.initOptions();
      } catch (err) {
        console.error('[Eucaloo] Variant data load error:', err);
      }
    }

    initOptions() {
      if (!this.productData) return;

      // Initialize selections from current variant
      this.productData.options.forEach((opt, idx) => {
        this.selections[opt] = this.currentVariant?.['option' + (idx + 1)] || null;
      });

      this.renderOptions();
      this.updateFromVariant(this.currentVariant);
    }

    renderOptions() {
      this.container.innerHTML = '';

      this.productData.options.forEach((optName, optIdx) => {
        const values = [...new Set(this.productData.variants.map(v => v['option' + (optIdx + 1)]))];
        const isColor = /colou?r/i.test(optName);

        const wrapper = document.createElement('div');
        wrapper.className = 'product-option';

        const label = document.createElement('div');
        label.className = 'product-option__label';
        const colorDot = isColor ? `<span class="product-option__color-dot" style="background:${getColorHex(this.selections[optName] || '')}"></span>` : '';
        label.innerHTML = `${optName}: <span class="product-option__selected">${this.selections[optName] || ''}</span>${colorDot}`;
        wrapper.appendChild(label);

        const btns = document.createElement('div');
        btns.className = isColor ? 'color-options' : 'option-buttons';

        values.forEach(val => {
          const available = this.isOptionAvailable(optName, val);

          if (isColor) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'color-option' + (val === this.selections[optName] ? ' active' : '') + (!available ? ' sold-out' : '');
            btn.style.backgroundColor = getColorHex(val);
            btn.title = val;
            btn.dataset.option = optName;
            btn.dataset.value  = val;
            btn.setAttribute('aria-label', val + (available ? '' : ' (sold out)'));
            btn.addEventListener('click', () => this.selectOption(optName, val, btn, label));
            btns.appendChild(btn);
          } else {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'option-btn' + (val === this.selections[optName] ? ' active' : '') + (!available ? ' sold-out' : '');
            btn.textContent = val;
            btn.dataset.option = optName;
            btn.dataset.value  = val;
            btn.setAttribute('aria-label', val + (available ? '' : ' (sold out)'));
            btn.addEventListener('click', () => this.selectOption(optName, val, btn, label));
            btns.appendChild(btn);
          }
        });

        wrapper.appendChild(btns);
        this.container.appendChild(wrapper);
      });
    }

    isOptionAvailable(optionName, optionValue) {
      const tempSelections = { ...this.selections, [optionName]: optionValue };
      return this.productData.variants.some(v => {
        return this.productData.options.every((opt, idx) => {
          const sel = tempSelections[opt];
          return !sel || v['option' + (idx + 1)] === sel;
        }) && v.available;
      });
    }

    selectOption(optionName, value, clickedBtn, labelEl) {
      this.selections[optionName] = value;

      // Update active state within this option group
      const group = clickedBtn.closest('.color-options, .option-buttons');
      group?.querySelectorAll('.color-option, .option-btn').forEach(b => b.classList.remove('active'));
      clickedBtn.classList.add('active');

      // Update label selected text
      const selectedSpan = labelEl.querySelector('.product-option__selected');
      if (selectedSpan) selectedSpan.textContent = value;

      // Update color dot if present
      const colorDot = labelEl.querySelector('.product-option__color-dot');
      if (colorDot) colorDot.style.background = getColorHex(value);

      // Find matching variant
      const variant = this.findVariant();
      this.currentVariant = variant;

      if (variant) {
        this.updateFromVariant(variant);
        // Re-render to update sold-out states for other options
        this.renderOptions();
      }
    }

    findVariant() {
      return this.productData.variants.find(v =>
        this.productData.options.every((opt, idx) => {
          const sel = this.selections[opt];
          return !sel || v['option' + (idx + 1)] === sel;
        })
      );
    }

    updateFromVariant(variant) {
      if (!variant) return;

      // Update hidden input
      if (this.variantInput) this.variantInput.value = variant.id;

      // Update price
      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(variant.price);
        this.priceEl.classList.toggle('on-sale', !!variant.compare_at_price && variant.compare_at_price > variant.price);
      }
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.textContent = formatMoney(variant.compare_at_price);
          this.compareEl.style.display = 'inline';
        } else {
          this.compareEl.style.display = 'none';
        }
      }

      // Update ATC button
      if (this.atcBtn) {
        if (variant.available) {
          this.atcBtn.disabled = false;
          this.atcBtn.textContent = 'Add to Cart';
          this.atcBtn.classList.remove('btn-ghost');
          this.atcBtn.classList.add('btn-primary');
        } else {
          this.atcBtn.disabled = true;
          this.atcBtn.textContent = 'Sold Out';
          this.atcBtn.classList.add('btn-ghost');
          this.atcBtn.classList.remove('btn-primary');
        }
      }

      // Show/hide quantity selector based on availability
      if (this.qtyWrapper) {
        this.qtyWrapper.style.display = variant.available ? '' : 'none';
      }

      // Stock message
      if (this.stockMsg) {
        const qty = this.inventoryMap[variant.id] ?? variant.inventory_quantity ?? 0;
        if (variant.available) {
          if (qty > 0 && qty <= 5) {
            this.stockMsg.textContent = `Only ${qty} left!`;
            this.stockMsg.style.color = 'var(--color-orange)';
            this.stockMsg.style.display = 'block';
          } else {
            this.stockMsg.style.display = 'none';
          }
        } else {
          this.stockMsg.style.display = 'none';
        }
      }

      // Last-in-stock / sold-out classes on ATC section
      if (this.atcSection) {
        const qty = this.inventoryMap[variant.id] ?? variant.inventory_quantity ?? 0;
        this.atcSection.classList.toggle('is-last-in-stock', variant.available && qty === 1);
        this.atcSection.classList.toggle('is-sold-out', !variant.available);
      }

      // Update gallery image
      if (this.galleryMain && variant.featured_image?.src) {
        const src = variant.featured_image.src.replace(/(\.\w+)$/, '_1600x$1');
        this.galleryMain.src = src;
        // Also update active thumb
        const thumbs = document.querySelectorAll('.product-gallery__thumb');
        thumbs.forEach(t => {
          t.classList.toggle('active', t.dataset.src === src || t.dataset.src?.includes(variant.featured_image.src.split('/').pop().split('.')[0]));
        });
      }

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Notify apps (e.g. Notify! Back in Stock) that the variant changed
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }
  }

  /* ---- Init all variant pickers on page ---- */
  document.querySelectorAll('[data-variant-picker]').forEach(el => new VariantPicker(el));
})();
